import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { Bunker84Instance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * BUNKER 84 — fifteenth cartridge (sc-4534, epic sc-4533).
 *
 * Wolfenstein 3D (1992) heritage retrofitted to a 1984 silhouette. A
 * software raycaster running entirely inside the Canvas2D cartridge
 * runtime — no WebGL, no engine swap. The cartridge writes columns
 * directly into a single reusable `ImageData` buffer and blits once
 * per frame via `putImageData`.
 *
 * **First 3D cartridge in the catalog.** The risk is not whether a
 * raycaster CAN run in Canvas2D (it can) but whether `putImageData`
 * at 480×270×60Hz holds the 16.6ms frame budget under real cartridge
 * load. v0.1 (sc-4534 → sc-4540) proves that with a flat-shaded
 * single-level loop. v0.2 layers in textures + sprite billboards.
 * v0.3 ships the full 3-level campaign with weapons + boss.
 *
 * Aesthetic: dim concrete, sodium-vapor amber emergency lamps,
 * monochrome guards — '80s underground-facility horror. Dodges the
 * Wolfenstein IP echo by leaning into the 1984 brand year and a
 * neutral "Bunker 84" facility code.
 *
 * v0.1 step plan:
 *   1. Scaffold — manifest + instance stub + black-screen title placeholder.
 *      **(this commit)**
 *   2. Map data + collision — 32×32 tile grid, player {x, y, angle},
 *      circle-vs-grid collision helper.
 *   3. DDA raycaster + flat-shaded column renderer (PERF GATE).
 *   4. Movement + turning + input mapping (WASD + arrows + virtual stick).
 *   5. Hitscan weapon + one enemy type + damage model.
 *   6. HUD pass + game-over flow + run length tuning.
 *   7. V0NN migration registering the cartridge in the scores registry.
 *
 * Resolution: 480×270 pixelated. Landscape-only per the catalog
 * constraint — every cartridge ships landscape.
 */
const module_: GameModule = {
  id: 'bunker-84',
  title: 'BUNKER 84',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'shooter',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'W / ↑', action: 'FORWARD' },
    { keys: 'S / ↓', action: 'BACK' },
    { keys: 'A / ←', action: 'TURN LEFT' },
    { keys: 'D / →', action: 'TURN RIGHT' },
    { keys: 'X + A/D', action: 'STRAFE (HOLD X)' },
    { keys: 'Z / SPACE', action: 'FIRE' },
    { keys: 'C', action: 'PERF HUD' },
  ],
  touch: {
    fireA: 'FIRE',
    fireB: 'STRAFE',
    fireC: 'PERF',
  },

  async preload(_ctx: PreloadContext): Promise<void> {
    // v0.1.1 scaffold ships no SFX yet — weapon + ambient audio land
    // alongside the gameplay steps that need them (v0.1.5, v0.2.4).
    // Keeping preload empty here means each subsequent step touches
    // exactly the scope it owns.
  },

  init(ctx: GameContext): GameInstance {
    return new Bunker84Instance(ctx);
  },
};

export default module_;
